import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import type {
  Attribute,
  ProductType,
  ProductTypeAttribute,
} from '@nucleus-commerce/api-client/admin';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Toggle } from '@nucleus-commerce/ui/components/toggle';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaRichTextEditor } from '@/features/media';
import { stripHtml } from '@/features/seo';

import {
  defaultDimensionUnit,
  emptyAttributeValue,
  toAttributeValuesPayload,
} from '../api/mappers';
import { useProductScope } from '../hooks/use-product-scope';
import {
  DIMENSION_UNITS,
  type AttributeFormValue,
  type ProductFormInput,
} from '../schemas/product-form-schema';

import { EditOnMasterLink, SharedChip } from './product-scope';

export interface AttributeValuesSectionProps {
  productType: ProductType | undefined;
  attributesById: ReadonlyMap<string, Attribute>;
  isLoading: boolean;
}

const NONE = '__none__';

interface AttributeFieldProps {
  assignment: ProductTypeAttribute;
  attribute: Attribute;
  slot: AttributeFormValue;
  error: string | undefined;
  /** Also an option of this product, so its value is set on each variant. */
  perVariant: boolean;
  onChange: (slot: AttributeFormValue) => void;
}

/** Whether saving would send a value for this attribute, the same test the save itself makes. */
function holdsValue(attribute: Attribute, slot: AttributeFormValue | undefined) {
  return (
    slot !== undefined && toAttributeValuesPayload({ [attribute.id]: slot }, [attribute]).length > 0
  );
}

function PerVariantNote() {
  const { t } = useTranslation('products');
  return (
    <span className="text-muted-foreground text-[13px]">{t('rail.attributes.perVariant')}</span>
  );
}

function AttributeField({
  assignment,
  attribute,
  slot,
  error,
  perVariant,
  onChange,
}: AttributeFieldProps) {
  const { t } = useTranslation('products');
  const set = (patch: Partial<AttributeFormValue>) => onChange({ ...slot, ...patch });
  const id = `attr-${attribute.id}`;
  const invalid = error !== undefined;
  const wide = attribute.dataType === 'rich_text';
  // The backend counts only product-level values towards a required attribute, not the option, so
  // a required one left empty keeps its input: the product could not go Active without it.
  const onVariantsOnly =
    perVariant && !error && (!assignment.isRequired || holdsValue(attribute, slot));

  const control = (() => {
    switch (attribute.dataType) {
      case 'select':
        return (
          <Select
            value={slot.ids[0] ?? NONE}
            onValueChange={(v) => set({ ids: v === NONE ? [] : [v] })}
          >
            <SelectTrigger id={id} className="w-full" aria-invalid={invalid}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>
                <span className="text-muted-foreground">{t('attributes.none')}</span>
              </SelectItem>
              {attribute.values.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.swatch?.color ? (
                    <span
                      className="inline-block size-3 rounded-full border"
                      style={{ background: v.swatch.color }}
                      aria-hidden
                    />
                  ) : null}
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'multiselect':
        return (
          <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby={`${id}-label`}>
            {attribute.values.map((v) => {
              const pressed = slot.ids.includes(v.id);
              return (
                <Toggle
                  key={v.id}
                  size="sm"
                  variant="outline"
                  pressed={pressed}
                  onPressedChange={(on) =>
                    set({ ids: on ? [...slot.ids, v.id] : slot.ids.filter((x) => x !== v.id) })
                  }
                  className="data-[state=on]:bg-primary-tint data-[state=on]:text-primary-tint-foreground data-[state=on]:border-primary/40 h-7 rounded-full px-2.5 text-xs"
                >
                  {v.label}
                </Toggle>
              );
            })}
          </div>
        );
      case 'number':
        return (
          <Input
            id={id}
            type="number"
            inputMode="decimal"
            step="any"
            value={slot.number}
            aria-invalid={invalid}
            onChange={(e) => set({ number: e.target.value })}
          />
        );
      case 'dimension':
        return (
          <div className="flex gap-2">
            <Input
              id={id}
              type="number"
              inputMode="decimal"
              step="any"
              min={0}
              value={slot.number}
              aria-invalid={invalid}
              className="min-w-0 flex-1"
              onChange={(e) => set({ number: e.target.value })}
            />
            <Select value={slot.unit || 'mm'} onValueChange={(unit) => set({ unit })}>
              <SelectTrigger
                className="w-20 shrink-0"
                aria-label={t('attributes.unitFor', { attribute: attribute.label })}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIMENSION_UNITS.map((unit) => (
                  <SelectItem key={unit} value={unit}>
                    {unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case 'rich_text':
        return (
          <MediaRichTextEditor
            id={id}
            value={slot.html}
            onChange={(html) => set({ html: html ?? '' })}
            aria-label={attribute.label}
            aria-invalid={invalid}
          />
        );
      case 'text':
        return (
          <Input
            id={id}
            value={slot.text}
            aria-invalid={invalid}
            onChange={(e) => set({ text: e.target.value })}
          />
        );
      case 'boolean':
        return (
          <div className="flex h-9 items-center gap-2">
            <Checkbox
              id={id}
              checked={slot.bool}
              aria-invalid={invalid}
              onCheckedChange={(v) => set({ bool: v === true })}
            />
            <Label htmlFor={id} className="font-normal">
              {t('attributes.yes')}
            </Label>
          </div>
        );
    }
  })();

  return (
    <div className={cn('flex min-w-0 flex-col gap-1', wide && !onVariantsOnly && 'md:col-span-2')}>
      <Label
        htmlFor={onVariantsOnly ? undefined : id}
        id={`${id}-label`}
        className={cn('text-xs', invalid && 'text-destructive')}
      >
        {attribute.label}
        {assignment.isRequired ? <span aria-hidden> *</span> : null}
        {attribute.unit && attribute.dataType === 'number' ? (
          <span className="text-muted-foreground font-normal">({attribute.unit})</span>
        ) : null}
      </Label>
      {onVariantsOnly ? <PerVariantNote /> : control}
      {perVariant && !onVariantsOnly ? <PerVariantNote /> : null}
      {error ? <p className="text-destructive text-xs">{t(error)}</p> : null}
    </div>
  );
}

/** An attribute's value as text, for a store's page; empty when it has none. */
function attributeValueText(
  attribute: Attribute,
  slot: AttributeFormValue,
  words: { yes: string; no: string },
): string {
  switch (attribute.dataType) {
    case 'select':
    case 'multiselect':
      return attribute.values
        .filter((value) => slot.ids.includes(value.id))
        .map((value) => value.label)
        .join(', ');
    case 'number':
      return slot.number === '' ? '' : [slot.number, attribute.unit].filter(Boolean).join(' ');
    case 'dimension':
      return slot.number === '' ? '' : [slot.number, slot.unit].filter(Boolean).join(' ');
    case 'rich_text':
      return stripHtml(slot.html);
    case 'text':
      return slot.text.trim();
    case 'boolean':
      return slot.bool ? words.yes : words.no;
  }
}

/**
 * One input per attribute of the chosen product type, in the type's order; required ones are
 * starred and counted in the chip. Slots live in `attributeValues` as one record so a type change
 * never leaves half-registered fields behind: the section writes the whole record on every change.
 */
export function AttributeValuesSection({
  productType,
  attributesById,
  isLoading,
}: AttributeValuesSectionProps) {
  const { t } = useTranslation('products');
  const { readOnly } = useProductScope();
  const { control, setValue, clearErrors, formState } = useFormContext<ProductFormInput>();
  const [slots, options] = useWatch({ control, name: ['attributeValues', 'options'] });
  const errors = formState.errors.attributeValues;
  const assignments = [...(productType?.attributes ?? [])].sort((a, b) => a.position - b.position);
  const optionAttributes = new Set(options.map((option) => option.attributeId));
  const required = assignments.filter(
    (row) => row.isRequired && attributesById.has(row.attributeId),
  );
  const missing = required.filter((row) => {
    const attribute = attributesById.get(row.attributeId);
    return attribute !== undefined && !holdsValue(attribute, slots[attribute.id]);
  }).length;

  const change = (attributeId: string, slot: AttributeFormValue) => {
    clearErrors(`attributeValues.${attributeId}`);
    setValue('attributeValues', { ...slots, [attributeId]: slot }, { shouldDirty: true });
  };

  return (
    <SummaryCard
      title={t('sections.attributes')}
      badge={<SharedChip />}
      action={
        <>
          {isLoading || required.length === 0 ? null : (
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs whitespace-nowrap',
                missing === 0
                  ? 'bg-success/15 text-success dark:bg-success/20'
                  : 'bg-warning/20 text-warning-foreground dark:bg-warning/25 dark:text-warning',
              )}
            >
              {missing === 0
                ? t('rail.attributes.allFilled')
                : t('rail.attributes.missing', { count: missing })}
            </span>
          )}
          {readOnly ? <EditOnMasterLink /> : null}
        </>
      }
      description={
        productType ? (
          <>
            {t('sections.attributesFrom')}{' '}
            <strong className="text-foreground font-medium">{productType.name}</strong>
          </>
        ) : (
          t('rail.attributes.noType')
        )
      }
    >
      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : productType && assignments.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2">
          {assignments.map((assignment) => {
            const attribute = attributesById.get(assignment.attributeId);
            if (!attribute) return null;
            const fieldError = errors?.[attribute.id];
            const slot =
              slots[attribute.id] ?? emptyAttributeValue(defaultDimensionUnit(attribute.unit));
            const perVariant = optionAttributes.has(attribute.id);
            if (readOnly) {
              const text = perVariant
                ? t('rail.attributes.perVariant')
                : holdsValue(attribute, slots[attribute.id])
                  ? attributeValueText(attribute, slot, {
                      yes: t('attributes.yes'),
                      no: t('rail.attributes.no'),
                    })
                  : '';
              return (
                <div key={attribute.id} className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-muted-foreground text-xs">{attribute.label}</span>
                  <span className={cn('text-[13px]', !text && 'text-muted-foreground')}>
                    {text || t('attributes.none')}
                  </span>
                </div>
              );
            }
            return (
              <AttributeField
                key={attribute.id}
                assignment={assignment}
                attribute={attribute}
                slot={slot}
                error={typeof fieldError?.message === 'string' ? fieldError.message : undefined}
                perVariant={perVariant}
                onChange={(next) => change(attribute.id, next)}
              />
            );
          })}
        </div>
      ) : productType ? (
        <p className="text-muted-foreground text-[13px]">{t('sections.attributesEmpty')}</p>
      ) : null}
    </SummaryCard>
  );
}
